import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  message,
  Space,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  useCoilWidthsQuery,
  useCreateCoilWidthMutation,
  useUpdateCoilWidthMutation,
  useDeleteCoilWidthMutation,
} from "../../Services";
import { CoilWidth } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface CoilWidthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newCoilWidth?: CoilWidth) => void;
}

export const CoilWidthModal: React.FC<CoilWidthModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: coilWidths = [], isLoading } = useCoilWidthsQuery({
    enabled: open,
  });

  const createMutation = useCreateCoilWidthMutation();
  const updateMutation = useUpdateCoilWidthMutation();
  const deleteMutation = useDeleteCoilWidthMutation();

  const handleStartEdit = (record: CoilWidth) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      width_ft: record.width_ft,
      width_mm: record.width_mm,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        updateMutation.mutate(
          {
            id: editingId,
            name: values.name.trim(),
            width_ft: values.width_ft ?? undefined,
            width_mm: values.width_mm ?? undefined,
          },
          {
            onSuccess: (res: any) => {
              if (res?.success) {
                message.success("Coil width updated successfully!");
                handleCancelEdit();
              } else {
                message.error(res?.message || "Failed to update coil width.");
              }
            },
            onError: () => message.error("Something went wrong."),
          },
        );
      } else {
        createMutation.mutate(values, {
          onSuccess: (res: any) => {
            if (res?.success) {
              message.success("Coil width config added successfully!");
              form.resetFields();
              if (onSuccess) {
                onSuccess(res?.data);
              }
            } else {
              message.error(res?.message || "Failed to add coil width.");
            }
          },
          onError: () => message.error("Something went wrong."),
        });
      }
    } catch {
      message.error("Please enter valid coil width details.");
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res: any) => {
        if (res?.success) {
          message.success("Coil width deleted successfully!");
          if (editingId === id) {
            handleCancelEdit();
          }
        } else {
          message.error(res?.message || "Failed to delete coil width.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const handleModalClose = () => {
    handleCancelEdit();
    onClose();
  };

  const columns = [
    {
      title: "Config Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: CoilWidth) => (
        <span
          className={`font-semibold text-xs ${
            record.id === editingId ? "text-blue-600" : "text-gray-800"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Width (ft)",
      dataIndex: "width_ft",
      key: "width_ft",
      width: 100,
      render: (val: number) => (
        <span className="font-mono text-xs">{val ? `${val} ft` : "—"}</span>
      ),
    },
    {
      title: "Width (mm)",
      dataIndex: "width_mm",
      key: "width_mm",
      width: 100,
      render: (val: number) => (
        <span className="font-mono text-xs">{val ? `${val} mm` : "—"}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center" as const,
      render: (_: any, record: CoilWidth) => (
        <Space size="small">
          <Button
            type="text"
            icon={
              <EditOutlined
                className={record.id === editingId ? "text-blue-600" : ""}
              />
            }
            size="small"
            onClick={() => handleStartEdit(record)}
          />
          <Popconfirm
            title="Delete this width config?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Coil Width Configurations")}
      open={open}
      onCancel={handleModalClose}
      footer={modalFooter({
        actions: [{ label: "Close", onClick: handleModalClose }],
      })}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        className="my-3 bg-slate-50 border border-slate-200 p-3 rounded-lg"
      >
        <div className="grid grid-cols-3 gap-3">
          <Form.Item
            name="name"
            label="Width Label / Name"
            rules={[{ required: true, message: "Label is required" }]}
            className="mb-0"
          >
            <Input
              placeholder="e.g. 3.00 ft (914 mm)"
              prefix={<PlusOutlined />}
            />
          </Form.Item>

          <Form.Item name="width_ft" label="Width (ft)" className="mb-0">
            <InputNumber
              className="w-full"
              placeholder="3.00"
              min={0}
              step={0.01}
            />
          </Form.Item>

          <Form.Item name="width_mm" label="Width (mm)" className="mb-0">
            <InputNumber
              className="w-full"
              placeholder="914.00"
              min={0}
              step={1}
            />
          </Form.Item>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <Button size="middle" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            size="middle"
            onClick={handleFormSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            className={
              editingId
                ? "bg-blue-600 hover:bg-blue-700 font-semibold"
                : "bg-emerald-600 hover:bg-emerald-700 font-semibold"
            }
          >
            {editingId ? "Update Coil Width Config" : "Add Coil Width Config"}
          </Button>
        </div>
      </Form>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={coilWidths}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </Modal>
  );
};

